/**
 * Reusable "scroll to target" behaviour. Any element carrying
 * `data-scroll-to="<css-selector>"` smooth-scrolls the page to that target on
 * click, landing it just below the sticky header stack. The header height is
 * measured live so it stays correct across the desktop ↔ mobile header swap.
 *
 * Lenis-driven when available (keeps GSAP ScrollTrigger in sync), with a native
 * `window.scrollTo` fallback. Delegated on `document`, so it survives ASwap view
 * swaps with no re-init.
 *
 * Markup: `<button data-scroll-to="#movieScreenings">…</button>`.
 */

// Sticky bars that pin to the viewport top — exactly one is visible per
// breakpoint (desktop `.header-nav` is `max-nav:hidden`; the mobile
// `.header-mobile-bar` is hidden above the nav breakpoint). A small gap below
// keeps the target from hugging the header edge.
const STICKY_HEADER_SELECTOR = '.header-nav, .header-mobile-bar';
const SCROLL_GAP = 20;

/** Height of the currently-visible sticky header, plus a breathing gap. */
const stickyHeaderOffset = (): number => {
  let offset = 0;
  document.querySelectorAll<HTMLElement>(STICKY_HEADER_SELECTOR).forEach((el) => {
    if (el.offsetParent === null) return; // display:none at this breakpoint
    offset = Math.max(offset, el.getBoundingClientRect().height);
  });
  return offset + SCROLL_GAP;
};

export const initScrollTo = (): void => {
  document.addEventListener('click', (e) => {
    const trigger = (e.target as HTMLElement | null)?.closest<HTMLElement>('[data-scroll-to]');
    if (!trigger) return;
    const selector = trigger.dataset.scrollTo;
    if (!selector) return;
    const target = document.querySelector<HTMLElement>(selector);
    if (!target) return;
    e.preventDefault();

    const offset = stickyHeaderOffset();
    const lenis = (window as any).lenis;
    if (lenis) {
      lenis.scrollTo(target, {
        offset: -offset,
        duration: 1,
        easing: (t: number) => (t === 1 ? 1 : 1 - Math.pow(2, -10 * t)), // easeOutExpo
      });
    } else {
      const top = target.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  });
};
